import { useQuery } from "@tanstack/react-query";

import {
  getAnalyticsPublishing,
  getAnalyticsTopContent,
  getAnalyticsTraffic,
  getGoogleAnalyticsSummary,
  getSearchConsoleSummary,
  listJudgeLog,
  type AnalyticsPublishing,
  type AnalyticsRange,
  type AnalyticsTopContent,
  type AnalyticsTraffic,
  type GoogleAnalyticsSummary,
  type SearchConsoleSummary,
} from "@/lib/admin-api";
import { bucketForRange, deriveJudgeVerdictMix, type JudgeVerdictMix } from "@/lib/analytics";

/**
 * One hook per Analytics section, so the sections fail independently.
 *
 * Each returns its own `data` and `failed`, and no hook can take another
 * section down with it: a section that cannot load shows one line where its
 * figures would be while the rest of the page keeps working. That is the whole
 * reason these are separate queries rather than one composed fetch.
 *
 * `failed` is distinct from empty. An empty series is data, and the sections
 * say so in their own words rather than showing the same line a failure does.
 */

export interface AnalyticsSection<T> {
  data: T | undefined;
  loading: boolean;
  failed: boolean;
}

export function useTrafficAnalytics(range: AnalyticsRange): AnalyticsSection<AnalyticsTraffic> {
  const { data, isPending, isError } = useQuery({
    queryKey: ["analytics", "traffic", range],
    queryFn: () => getAnalyticsTraffic({ range, bucket: bucketForRange(range) }),
  });
  return { data, loading: isPending, failed: isError };
}

export function useTopContentAnalytics(
  range: AnalyticsRange,
  limit: number,
): AnalyticsSection<AnalyticsTopContent> {
  const { data, isPending, isError } = useQuery({
    queryKey: ["analytics", "top-content", range, limit],
    queryFn: () => getAnalyticsTopContent({ range, limit }),
  });
  return { data, loading: isPending, failed: isError };
}

export function usePublishingAnalytics(
  range: AnalyticsRange,
): AnalyticsSection<AnalyticsPublishing> {
  const { data, isPending, isError } = useQuery({
    queryKey: ["analytics", "publishing", range],
    queryFn: () => getAnalyticsPublishing({ range, bucket: bucketForRange(range) }),
  });
  return { data, loading: isPending, failed: isError };
}

/**
 * The Google Analytics section.
 *
 * Follows the page's own window selector rather than carrying a second one:
 * two selectors on one page is two states an operator has to keep in their
 * head, and the endpoint accepts the same 7d/30d/90d vocabulary the native
 * sections use precisely so this can share it.
 *
 * `failed` here means the request failed. It does NOT mean unconfigured, which
 * arrives as a successful response with `configured: false`. The section
 * renders those two as different lines, because one is a deployment that was
 * never switched on and the other is something being wrong.
 */
export function useGoogleAnalytics(
  range: AnalyticsRange,
): AnalyticsSection<GoogleAnalyticsSummary> {
  const { data, isPending, isError } = useQuery({
    queryKey: ["analytics", "google", range],
    queryFn: () => getGoogleAnalyticsSummary({ range }),
  });
  return { data, loading: isPending, failed: isError };
}

/**
 * The Search Console section. Same follows-the-page-selector and
 * configured-vs-failed-vs-empty contract as useGoogleAnalytics.
 */
export function useSearchConsole(range: AnalyticsRange): AnalyticsSection<SearchConsoleSummary> {
  const { data, isPending, isError } = useQuery({
    queryKey: ["analytics", "search-console", range],
    queryFn: () => getSearchConsoleSummary({ range }),
  });
  return { data, loading: isPending, failed: isError };
}

/**
 * The judge verdict mix, built client-side from three totalCount reads.
 *
 * Same page_size: 1 / totalCount trick as use-home-counts.ts: the list endpoint
 * already returns `pageInfo.totalCount`, so three cheap requests give the mix
 * without pulling any rows.
 *
 * Three, not one per verdict, because `verdict` is FREE TEXT server-side.
 * Nothing bounds the set of values, so only "clean" and "spam" are asked for by
 * name and the remainder is derived from the total. Adding a query per verdict
 * would silently omit any verdict string nobody thought to list.
 *
 * Returns undefined rather than a fabricated zero when any of the three totals
 * is missing: a page_size: 1 response's items.length is not a stand-in for a
 * total, and a mix with one leg guessed is not a mix.
 */
export function useJudgeVerdictMix(): AnalyticsSection<JudgeVerdictMix> {
  const { data, isPending, isError } = useQuery({
    queryKey: ["analytics", "judge-verdict-mix"],
    queryFn: async () => {
      const [all, clean, spam] = await Promise.all([
        listJudgeLog({ pageSize: 1, page: 1 }),
        listJudgeLog({ verdict: "clean", pageSize: 1, page: 1 }),
        listJudgeLog({ verdict: "spam", pageSize: 1, page: 1 }),
      ]);

      const total = all.pageInfo?.totalCount;
      const cleanTotal = clean.pageInfo?.totalCount;
      const spamTotal = spam.pageInfo?.totalCount;
      if (total == null || cleanTotal == null || spamTotal == null) {
        // react-query treats `undefined` from a queryFn as a usage error, so
        // `null` is the sentinel that comes back out of the cache, translated
        // to `undefined` below.
        return null;
      }
      return deriveJudgeVerdictMix(total, cleanTotal, spamTotal);
    },
  });

  return { data: data ?? undefined, loading: isPending, failed: isError };
}
